/**
 * The owner's way back, from wherever they are (IMPL-ENGAGE-002 §6.2, §6.5).
 *
 * ┌─ THE QUESTIONNAIRE WAS A ONE-WAY DOOR. ─────────────────────────────────┐
 * │ Nothing in the wizard linked to the engagement's own home or to          │
 * │ delegation. An owner halfway through, realising a chapter belongs to     │
 * │ their cloud lead, had no route to the screen that hands it over — the    │
 * │ only paths to `/engagements/:ref/people` were the hub, reachable only by │
 * │ going back to the chooser and re-picking the engagement they were        │
 * │ already in.                                                              │
 * │                                                                          │
 * │ So the feature built for exactly that moment was unreachable from the    │
 * │ moment it is needed.                                                     │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * A link to the page you are on is a link that does nothing, so each is
 * dropped on its own destination rather than rendered inert.
 */
import type { ReachableEngagement } from '@nimer/contracts/person'

import { useLocationPath } from '../app/navigation.ts'
import { ownerPathFor, peoplePathFor } from './routes.ts'
import { t, type MessageKey } from '../shell/i18n.ts'

const QUESTIONNAIRE_PATH = '/questionnaire'

export interface BarLink {
  readonly href: string
  readonly label: MessageKey
}

/**
 * What this person can do with the engagement they are on.
 *
 * Delegation is the owner's alone: a contributor forwarding work on would be
 * re-assigning what somebody asked them personally to answer, and §6.5 gives
 * the fork to the owner.
 */
export function linksFor(current: ReachableEngagement, path: string): readonly BarLink[] {
  const home = ownerPathFor(current.customer_ref)
  const people = peoplePathFor(current.customer_ref)

  return [
    { href: home, label: 'chooser.engagementHome' as MessageKey },
    ...(current.is_owner ? [{ href: people, label: 'chooser.bringColleagues' as MessageKey }] : []),
    // Offered only from outside: inside the wizard its own chrome is the way
    // around, and a "continue" pointing at the current screen is noise.
    ...(path.startsWith(QUESTIONNAIRE_PATH)
      ? []
      : [{ href: QUESTIONNAIRE_PATH, label: 'chooser.continueAnswering' as MessageKey }]),
  ].filter((link) => link.href !== path)
}

export function EngagementActions({ current }: { readonly current: ReachableEngagement | null }) {
  const path = useLocationPath()
  if (current === null) return null
  const links = linksFor(current, path)
  if (links.length === 0) return null

  return (
    <nav className="person-actions" aria-label={t('chooser.engagementNav')}>
      {links.map((link) => (
        <a key={link.href} className="person-actions__link" href={link.href}>
          {t(link.label)}
        </a>
      ))}
    </nav>
  )
}
